import { useCallback } from "react";
import { useMatch } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { useInboxQueryStates } from "../providers/inbox-query-states-provider";
import { useBook } from "../providers/use-book";
import { useConversation } from "../providers/use-conversation";
import { useInboxInsured } from "../providers/use-inbox-insured";
import { usePage } from "../providers/use-page";
import {
  INBOX_RAIL_LAYOUT,
  INBOX_RAIL_LAYOUT_STORAGE_KEY,
  type InboxRailLayout,
  isInboxRailLayout,
} from "./inbox-rail-layout";

/**
 * The three places the mini-app `<Outlet />` can live: the right-hand shell
 * panel, the left rail in place of the Book, or offscreen so the child route
 * stays mounted while nothing displays it. Exactly one of them holds it at a
 * time — a second live `MiniApp` means duplicate `SetMetadata`, duplicate
 * queries, and a document title flickering between two values.
 */
export const INBOX_OUTLET_HOST = {
  hidden: "hidden",
  miniApp: "mini-app",
  rail: "rail",
} as const;

export type InboxOutletHost = (typeof INBOX_OUTLET_HOST)[keyof typeof INBOX_OUTLET_HOST];

/**
 * The stored rail preference. Split from `useInboxRail` so the settings toggle
 * and the `MiniAppToggle` it replaces can read it without subscribing to the
 * conversation and insured state they have no other use for — the settings
 * dialog in particular sits above the inbox routes and has neither.
 */
export const useInboxRailLayout = () => {
  const [storedRailLayout, setStoredRailLayout] = useLocalStorage<string>(
    INBOX_RAIL_LAYOUT_STORAGE_KEY,
    INBOX_RAIL_LAYOUT.split
  );
  const railLayout = isInboxRailLayout(storedRailLayout) ? storedRailLayout : INBOX_RAIL_LAYOUT.split;
  const setRailLayout = useCallback(
    (nextRailLayout: InboxRailLayout) => {
      setStoredRailLayout(nextRailLayout);
    },
    [setStoredRailLayout]
  );

  return {
    isMergedRail: railLayout === INBOX_RAIL_LAYOUT.merged,
    railLayout,
    setRailLayout,
  };
};

/**
 * Resolves the rail mode and the current selection into the single home for the
 * mini-app `<Outlet />`. `InboxLayout` and `LeftSidebar` both read this rather
 * than deciding for themselves, so the "exactly one host" invariant holds by
 * construction instead of by two components agreeing.
 *
 * It also reports whether merged mode is actually in effect here, which is what
 * the email header needs to know to offer linking in the one layout where the
 * mini-app is not around to offer it, and owns both ends of the Back-to-book
 * round trip: the pin behind the rail header's control, and the release behind
 * the Book header's, so the two cannot disagree about when there is a way back.
 */
export const useInboxRail = () => {
  const { isBookOpen, setIsBookViewExpanded } = useBook();
  const { inboxConversation, loading } = useConversation();
  const { insured, insuredId } = useInboxInsured();
  const { conversationId: selectedConversationId, searchTerm } = useInboxQueryStates();
  const { isBookPinnedInRail, isMiniAppOpen, pinBookInRail: pinBookForSelection, unpinBookInRail } = usePage();
  const { isMergedRail: isMergedRailPreferred } = useInboxRailLayout();
  // The merged rail puts the mini-app `<Outlet />` in the Book's slot, so it
  // only applies where that outlet exists: the inbox route owns `miniAppRoutes`.
  // Drafts renders the same `LeftSidebar` with no child routes (B-1820), so the
  // stored preference stays off there however it was set.
  const isInboxRoute = Boolean(useMatch("/inbox/*"));
  const isMergedRail = isMergedRailPreferred && isInboxRoute;

  const selectedConversation = selectedConversationId
    ? inboxConversation?.id === selectedConversationId
      ? inboxConversation
      : undefined
    : inboxConversation;
  const hasMiniAppContext = Boolean(selectedConversation || insuredId || (selectedConversationId && loading));
  // Taking the rail costs the broker their Book, so merged mode only does it
  // once there is an insured to put there. An email with nothing linked keeps
  // the Book and offers `ConversationLinkInsuredButton` beside the star
  // instead; linking refetches the conversation, `insuredId` lands, and the
  // rail swaps itself in.
  //
  // While a newly selected conversation loads, `useInboxInsured` reports no
  // insured yet. Falling back to the conversation Apollo is still holding keeps
  // the Book from flashing in and out between two insured emails.
  const pendingInsuredId = selectedConversationId && loading ? inboxConversation?.insuredId : undefined;
  const hasRailInsured = Boolean(insuredId ?? pendingInsuredId);
  // The two things that outrank the insured and put the Book back in the rail
  // without touching the selection, which is what lets the broker's open email
  // survive either of them.
  //
  // A committed `?term=` is the higher of the two: results are a list the broker
  // works through, and opening one would otherwise swap out the Book holding
  // them. It is also the only thing that covers a search run from
  // `/inbox/:insuredId`, where the insured stays in the route.
  const bookOutranksInsured = Boolean(searchTerm) || isBookPinnedInRail;
  // `MiniAppToggle` is gone in merged mode, so `BookToggle` is what shows and
  // hides the rail — `isBookOpen`, not `isMiniAppOpen`, decides whether the
  // insured has anywhere to render.
  const railCanShowInsured = isMergedRail && isBookOpen && hasRailInsured;
  const showInsuredInRail = railCanShowInsured && !bookOutranksInsured;
  const hasMiniAppPanel = !isMergedRail && isMiniAppOpen && hasMiniAppContext;
  // Of the two overrides, the pin is the one the Book cannot be talked out of:
  // a committed term is still sitting in the search box to be cleared, but the
  // pin only lifts when the selection moves. So for as long as the broker stays
  // on the email they pressed Back to book from, this is the only way back to
  // its insured — which is why the Book header offers it whenever it is true.
  const isInsuredHeldByPin = railCanShowInsured && isBookPinnedInRail && !searchTerm;

  // The pin hands the slot back to the Book, and `LeftSidebar` re-applies the
  // book-view collapse the moment it does — so a broker who had collapsed the
  // Book earlier lands on its 3rem header rather than the Book they asked for.
  // Expanding is what a committed search already does for the same reason.
  const pinBookInRail = useCallback(() => {
    setIsBookViewExpanded(true);
    pinBookForSelection();
  }, [pinBookForSelection, setIsBookViewExpanded]);

  return {
    isInsuredHeldByPin,
    isMergedRail,
    outletHost: showInsuredInRail
      ? INBOX_OUTLET_HOST.rail
      : hasMiniAppPanel
        ? INBOX_OUTLET_HOST.miniApp
        : INBOX_OUTLET_HOST.hidden,
    pinBookInRail,
    // Both off the resolved record, never `pendingInsuredId`. That fallback exists
    // to hold the rail steady between two insured emails, and it names the
    // *outgoing* one — good enough to keep the Book from flashing, wrong to
    // describe a row with. Taking the id from it and the name from `insured` would
    // also let the two disagree: the previous insured's state and date beside no
    // name at all. Null here just means the row waits on its neutral fallback.
    railInsuredId: insured?.id ?? null,
    railInsuredName: insured?.name ?? null,
    returnToInsured: unpinBookInRail,
  };
};
